import Axios from 'axios'
import { hourKey } from './energyHistory'

// EnergyZero exposes Dutch EPEX day-ahead prices through a public, keyless
// API (the same source the playground benchmark used). It serves a full year
// of hourly prices in a single request, so no chunking is needed.
const ENERGY_ZERO_URL = 'https://api.energyzero.nl/v1/energyprices'

interface EnergyZeroResponse {
  Prices?: Array<{ price: number; readingDate: string }>
}

/**
 * Where the price fetch starts: a full year before `to`, or earlier if the meter
 * window is longer. A short history is completed to a year with the NED profile,
 * and those filled-in hours need their real EPEX hour too — a month/hour average
 * flattens the day-to-day spread trading lives on, and months outside the window
 * would get one flat price. Day-ahead prices are public and a year is one request.
 */
export function priceWindowStart(meterFrom: Date, to: Date): Date {
  const yearBefore = new Date(to)
  yearBefore.setFullYear(yearBefore.getFullYear() - 1)
  return meterFrom < yearBefore ? new Date(meterFrom) : yearBefore
}

/** Number of HTTP requests fetchHourlyPrices will make. */
export function priceRequestCount(): number {
  return 1
}

/**
 * Fetch hourly EPEX spot prices (EUR/kWh incl. BTW) for a period from
 * EnergyZero, keyed by UTC hour ISO so they align with the hourly energy
 * slots. interval=4 = hourly, usageType=1 = electricity consumption.
 */
export async function fetchHourlyPrices(
  from: Date,
  to: Date,
  options: { signal: AbortSignal; onRequestDone?: () => void }
): Promise<Map<string, number>> {
  const res = await Axios.get<EnergyZeroResponse>(ENERGY_ZERO_URL, {
    params: {
      fromDate: from.toISOString(),
      tillDate: to.toISOString(),
      interval: 4,
      usageType: 1,
      inclBtw: true,
    },
    signal: options.signal,
  })
  const map = new Map<string, number>()
  for (const p of res.data.Prices ?? []) {
    map.set(hourKey(p.readingDate), p.price)
  }
  options.onRequestDone?.()
  return map
}
